import type { FC } from 'react';
import { memo, type ReactNode } from 'react';
import { ArrowForward, Difference } from '@mui/icons-material';
import { Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getFontColor } from 'utils/getFontColor.ts';
import { CustomBadge } from 'components/mui/common/CustomBadge';
import { toTestIdSegment } from 'utils/testId';
import {
  Actions,
  Content,
  IconWrapper,
  StyledAnchor,
  StyledLink,
  StyledLinkButtonWrapper,
  StyledRemove,
  StyledSettings,
  Title,
  TitleWrapper,
  Wrapper,
} from './SimpleWidgetCard.styles';

interface Props {
  name?: string;
  count?: number;
  className?: string;
  children?: ReactNode;
  edit?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  link?: string;
  backgroundColor?: string;
  hasCustomLink?: boolean;
  unreadNotificationsCount?: number;
  dataTestId?: string;
}

const isProbablyLink = (text: string): boolean => {
  const pattern: RegExp = /^(https?:\/\/)?(www\.)?[a-z0-9._~+-]+(\.[a-z]{2,})(\/[^\s]*)?$/i;

  return pattern.test(text?.trim?.());
};

const LinkWrapper: FC<{ link?: string; children: ReactNode }> = ({ link, children }) => {
  if (link && !isProbablyLink(link)) return <StyledLink to={`/user/${link}`}>{children}</StyledLink>;

  const finalUrl = link?.startsWith('http') ? link : `https://${link}`;

  if (!link) {
    return <>{children}</>;
  }

  return (
    <StyledAnchor href={finalUrl} target="_blank" rel="noreferrer noopener">
      {children || link}
    </StyledAnchor>
  );
};

export const SimpleWidgetCard = memo(
  ({
    name,
    count,
    className,
    children,
    edit = false,
    onClick,
    link,
    hasCustomLink,
    backgroundColor,
    onRemove,
    unreadNotificationsCount,
    dataTestId,
  }: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isCount = !name?.includes(t('user.overview.unfilled'));
    const testId = dataTestId ?? `user-dashboard-simple-widget-${toTestIdSegment(name ?? link ?? 'item')}`;

    return (
      <Wrapper className={className} style={{ backgroundColor }} data-testid={testId}>
        <IconWrapper>
          <Difference />
        </IconWrapper>

        <LinkWrapper link={link}>
          <Content>
            <TitleWrapper>
              <Title fontWeight={600} style={{ color: backgroundColor ? getFontColor(backgroundColor) : theme.palette.text.primary }}>
                {name ?? t('user.overview.unfilled')}
              </Title>
              {!!unreadNotificationsCount && (
                <CustomBadge data-testid={`${testId}-badge`} sx={{ flexShrink: 0, ml: 0 }}>
                  {unreadNotificationsCount}
                </CustomBadge>
              )}
            </TitleWrapper>

            <Actions>
              {!edit && isCount && (
                <Typography variant="h5" style={{ color: getFontColor(backgroundColor) }}>
                  {count}
                </Typography>
              )}
              {!edit && hasCustomLink && (
                <StyledLinkButtonWrapper data-testid={`${testId}-show-all`}>
                  <ArrowForward fontSize="small" />
                </StyledLinkButtonWrapper>
              )}
              {edit && <StyledSettings onClick={onClick} />}
              {edit && <StyledRemove onClick={onRemove} />}
              {children}
            </Actions>
          </Content>
        </LinkWrapper>
      </Wrapper>
    );
  }
);
